import { format, parse } from 'date-fns';
import floor from 'lodash/floor';

export const MIN_DATE = new Date(-8640000000000000);

export const isValidDate = (date: Date): boolean => {
  return date instanceof Date && date && !isNaN(date.getTime());
};

export const fromISODate = (isoDate: string, fallback?: Date): Date => {
  if (!isoDate) {
    return fallback;
  }
  try {
    const date = new Date(isoDate);
    if (!isValidDate(date)) {
      return fallback;
    }
    return date;
  } catch (e) {
    return fallback;
  }
};

export const fromFormattedString = (dateTimeString: string, formatString: string = 'MM-dd-yyyy HH:mm:ss'): Date => {
  return parse(dateTimeString, formatString, new Date());
};

export const formatShortDate = (date: Date, fallback = '-'): string => {
  if (!isValidDate(date)) {
    return fallback;
  }
  return format(date, 'MM-dd-yyyy');
};

export const formatShortDateTime = (date: Date, fallback = '-'): string => {
  if (!isValidDate(date)) {
    return fallback;
  }
  return format(date, 'MM-dd-yyyy hh:mm a');
};

export const formatMonthYear = (date: Date, fallback = '-'): string => {
  if (!isValidDate(date)) {
    return fallback;
  }
  return format(date, 'MM-yyyy');
};

export const formatShortMonthName = (date: Date, fallback = '-'): string => {
  if (!isValidDate(date)) {
    return fallback;
  }
  return format(date, 'MMM');
};

export const formatShortDateFromISODate = (isoDate: string): string => {
  return formatShortDate(fromISODate(isoDate));
};

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/**
 * Format a calendar day key (`YYYY-MM-DD`) for display without timezone conversion.
 * Use for Tableau/API day buckets that are already truncated calendar dates — do not
 * pass these through `new Date(dateOnly)`, which treats them as UTC midnight and
 * shifts the label in US timezones.
 */
export const formatCalendarDayKey = (dateKey: string, fallback = '-'): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateKey.trim());
  if (!match) return fallback;
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return fallback;
  return `${SHORT_MONTHS[month - 1]} ${day}`;
};

/**
 * Captures the year, month and day of an API day bucket's `YYYY-MM-DD` prefix.
 * Day buckets must be parsed textually like this rather than through
 * `new Date(dateOnly)`, which reads them as UTC midnight and shifts the label
 * back a day in US timezones. The formatter above keeps the pattern inline so
 * it stays byte-identical to its origin copy; new code shares this constant.
 */
export const CALENDAR_DAY_KEY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * Numeric day-bucket label for dense chart axes: `MM/DD`, or `MM/DD/YY` when
 * the caller has established that the plotted range crosses a year boundary,
 * making the year necessary to tell two buckets apart.
 */
export const formatNumericDayKey = (dateKey: string, withYear = false, fallback = '-'): string => {
  const match = CALENDAR_DAY_KEY_PATTERN.exec(dateKey.trim());
  if (!match) return fallback;
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return fallback;
  return withYear ? `${match[2]}/${match[3]}/${match[1].slice(2)}` : `${match[2]}/${match[3]}`;
};

// Renders an ISO timestamp as "MM/DD/YYYY hh:mm AM/PM ET" anchored on
// America/New_York. Used by Report Schedules / Report History tables — the
// product currently surfaces all schedule times in US Eastern Time.
const ET_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: true,
});

export const formatEasternDateTime = (isoDateOrNull: string | null | undefined, fallback = '—'): string => {
  if (!isoDateOrNull) return fallback;
  const d = new Date(isoDateOrNull);
  if (Number.isNaN(d.getTime())) return fallback;
  // Intl emits "MM/DD/YYYY, hh:mm AM" — drop the comma and append "ET" for clarity.
  return `${ET_FORMATTER.format(d).replace(',', '')} ET`;
};

export function convertMinutesToHHMM(minutes: number): string {
  if (isNaN(minutes)) {
    return '0m';
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const formattedHours = hours === 0 ? '' : `${hours}h`;
  const formattedMins = `${Math.floor(mins)}m`;
  return `${formattedHours} ${formattedMins}`;
}

export function formatRelativeDate(date: Date, includeTime = true): string {
  if (!isValidDate(date)) {
    return '-';
  }
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);

  if (date.toDateString() === today.toDateString()) {
    return includeTime ? `Today ${format(date, 'h:mm a')}` : 'Today';
  } else if (date.toDateString() === yesterday.toDateString()) {
    return includeTime ? `Yesterday ${format(date, 'h:mm a')}` : 'Yesterday';
  } else if (date.toDateString() === tomorrow.toDateString()) {
    return includeTime ? `Tomorrow ${format(date, 'h:mm a')}` : 'Tomorrow';
  } else {
    return format(date, 'MM-dd-yyyy h:mm a');
  }
}

export function formatRelativePeriod(date: Date): string {
  if (!isValidDate(date)) {
    return '-';
  }
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  const weeks = Math.floor(diff / 604800000); // 7 days in ms
  const months = Math.floor(diff / 2592000000); // 30 days in ms
  const years = Math.floor(diff / 31536000000); // 365 days in ms

  if (minutes < 1) {
    return 'just now';
  }
  if (hours < 1) {
    return `${minutes} min${minutes > 1 ? 's' : ''} ago`;
  }
  if (days < 1) {
    return `${hours} hr${hours > 1 ? 's' : ''} ago`;
  }
  if (weeks < 1) {
    return `${days} day${days > 1 ? 's' : ''} ago`;
  }
  if (months < 1) {
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  }
  if (years < 1) {
    return `${months} month${months > 1 ? 's' : ''} ago`;
  }

  return `${years} year${years > 1 ? 's' : ''} ago`;
}

export function groupSectionFromIsoDate(date: Date): { name: string; dateTime: string } {
  let sectionName = '';
  let dateTime = '';

  const dateInMilliseconds = date.getTime();
  const now = Date.now();

  const _hourInMilliseconds = 60 * 60 * 1000;
  const _24HoursInMilliseconds = 24 * _hourInMilliseconds;
  const _1WeekInMilliseconds = 7 * _24HoursInMilliseconds;

  const withinOneDayInMilliseconds = now - _24HoursInMilliseconds;
  const withinOneWeekInMilliseconds = now - _1WeekInMilliseconds;

  if (dateInMilliseconds >= withinOneDayInMilliseconds) {
    const dateHours = date.getUTCHours() < 10 ? '0' + date.getUTCHours() : date.getUTCHours();
    const dateMinutes = date.getUTCMinutes() < 10 ? '0' + date.getUTCMinutes() : date.getUTCMinutes();

    sectionName = 'Today';
    dateTime = `${dateHours}:${dateMinutes}`;
  } else if (dateInMilliseconds < withinOneDayInMilliseconds && dateInMilliseconds >= withinOneWeekInMilliseconds) {
    const roundedDayDifference = floor((now - dateInMilliseconds) / _24HoursInMilliseconds);
    const splittedDate = formatShortDate(date);
    const dayDifference = roundedDayDifference > 1 ? `${splittedDate}` : 'Yesterday';
    const dateHours = date.getUTCHours() < 10 ? '0' + date.getUTCHours() : date.getUTCHours();
    const dateMinutes = date.getUTCMinutes() < 10 ? '0' + date.getUTCMinutes() : date.getUTCMinutes();

    sectionName = 'Recent';
    dateTime = `${dayDifference}, ${dateHours}:${dateMinutes}`;
  } else {
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    const splittedDate = formatShortDate(date);
    const month = date.getMonth();

    sectionName = months[month];
    dateTime = `${splittedDate}`;
  }

  return {
    name: sectionName,
    dateTime,
  };
}

export function convertISODateToDate(isoDate: string): Date {
  if (!isoDate) return null;

  const split = isoDate.split('T');

  const year = parseInt(split[0].split('-')[0]);
  const month = parseInt(split[0].split('-')[1]);
  const date = parseInt(split[0].split('-')[2]);

  const hour = parseInt(split[1].split(':')[0]);
  const minute = parseInt(split[1].split(':')[1]);
  const second = parseInt(split[1].split(':')[2]);

  const converted = new Date();
  converted.setFullYear(year, month - 1, date);
  converted.setHours(hour, minute, second);

  return converted;
}

export const getMonthFromDateString = (dateString: string, count: string | number = 'full'): string => {
  const dateParts = dateString.split('-');
  if (dateParts.length !== 3) {
    return 'Invalid Date Format';
  }

  const year = parseInt(dateParts[0], 10);
  const month = parseInt(dateParts[1], 10);
  const day = parseInt(dateParts[2], 10);

  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    return 'Invalid Date Parts';
  }

  const date = new Date(year, month - 1, day); // Month is 0-indexed

  if (isNaN(date.getTime())) {
    return 'Invalid Date';
  }

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const currentMonth =
    count === 'full' ? months[date.getMonth()] : months[date.getMonth()].substring(0, count as number);

  return currentMonth;
};
